<template>
    <div class="h-100 w-100 overflow-hidden d-flex align-items-start">
        <DrawToolbar v-if="drawPanelOpen" :activeTool="activeTool" @select="onSelectTool" @undo="engineUndo"
            @clear="engineClear" @exit="engineExit" />
        <div class="position-relative flex-grow-1 h-100 w-100">
            <CandlesChartEngine ref="engine" class="h-100 w-100" :tf="tf" :chartType="chartType"
                :renkoBrick="renkoBrick" :crosshairOn="crosshairBtnOn" @ready="onEngineReady"
                @contextmenu="onChartContextMenu" @crosshair-change="onCrosshairChange"
                @table-data="$emit('candles', $event)" @table-columns="$emit('table-columns', $event)" />
            <CandlesChartDrawLayer v-if="engineCtx" ref="draw" :chart="engineCtx.chart" :rootEl="engineCtx.rootEl"
                :getBars="engineCtx.getBars" :getMainPaneId="engineCtx.getMainPaneId"
                :getIndexByTs="engineCtx.getIndexByTs" />
            <ChartInfoCard ref="infoCard" :show="infoOn && infoBox.visible" :box="infoBox" />
        </div>
        <CandlesChartStudies ref="studies" :chart="chart" :mainPaneId="mainPaneId" :timeframes="timeFrames"
            :chartTypes="chart_types" :tf="tf" :chartType="chartType" :renkoBrick="renkoBrick"
            @select-timeframe="setTimeframe" @select-chart-type="setChartType" @apply-chart-type="applyChartType"
            @update:renkoBrick="renkoBrick = $event" @studies-changed="onStudiesChanged" />
    </div>
</template>
<script>
import ChartInfoCard from "./ChartInfoCard.vue";
import CandlesChartEngine from "./CandlesChartEngine.vue";
import CandlesChartStudies from "./CandlesChartStudies.vue";
import DrawToolbar from "./DrawToolbar.vue";
import CandlesChartDrawLayer from "./CandlesChartDrawLayer.vue";
export default {
    name: "CandlesChart",
    components: { ChartInfoCard, CandlesChartEngine, CandlesChartStudies, DrawToolbar, CandlesChartDrawLayer },
    emits: ["toolbar-state", "candles", "table-columns", "studies-change"],
    data() {
        return {
            chart: null,
            mainPaneId: "candle_pane",
            crosshairBtnOn: false,
            infoOn: false,
            selectedSymbol: "SILVER",
            timeFrames: [
                { key: "1m", label: "1m", period: { type: "minute", span: 1 } },
                { key: "2m", label: "2m", period: { type: "minute", span: 2 } },
                { key: "5m", label: "5m", period: { type: "minute", span: 5 } },
                { key: "15m", label: "15m", period: { type: "minute", span: 15 } },
                { key: "30m", label: "30m", period: { type: "minute", span: 30 } },
                { key: "1h", label: "1h", period: { type: "hour", span: 1 } },
                { key: "1D", label: "1D", period: { type: "day", span: 1 } },
                { key: "1W", label: "1W", period: { type: "week", span: 1 } },
                { key: "1M", label: "1MO", period: { type: "month", span: 1 } },
            ],
            tf: null,
            chart_types: [
                { key: "candle_solid", label: "Candle" },
                { key: "candle_stroke", label: "Hollow Candle" },
                { key: "colored_bar", label: "Colored Bar" },
                { key: "area", label: "Mountain / Area" },
                { key: "bar", label: "Bar (OHLC)" },
                { key: "line", label: "Line" },
                { key: "step", label: "Step" },
                { key: "colored_hlc_bar", label: "Colored HLC Bar" },
                { key: "scatter", label: "Scatterplot" },
                { key: "histogram", label: "Histogram" },
            ],
            chartType: null,
            renkoBrick: 200,
            infoBox: {
                visible: false,
                left: 0,
                top: 0,
                timeText: "",
                open: "",
                high: "",
                low: "",
                close: "",
                volume: "",
            },
            drawPanelOpen: false,
            activeTool: "",
            drawStatus: "Pick a tool and click on chart to draw",
            engineCtx: null,
        };
    },
    computed: {
        tfLabel() {
            return this.tf?.label ?? "1D";
        },
        chartTypeLabel() {
            return this.chartType?.label ?? "Candle";
        },
    },
    created() {
        this.tf = this.timeFrames.find((x) => x.key === "1D");
        this.chartType = this.chart_types[0];
    },
    methods: {
        setDrawPanelOpen(open) {
            this.drawPanelOpen = !!open;
            if (!this.drawPanelOpen) this.exitDrawMode();
        },
        onSelectTool(t) {
            this.activeTool = t.key;
            this.$refs.draw?.startDrawing?.(t.overlayName, (msg) => console.log(msg));
        },
        engineUndo() { this.$refs.draw?.undoDrawing?.(); },
        engineClear() { this.$refs.draw?.clearDrawings?.(); },
        engineExit() { this.$refs.draw?.stopDrawing?.(); },
        exitDrawMode() {
            this.activeTool = "";
            this.drawStatus = "Pick a tool and click on chart to draw";
            this.$refs.draw?.stopDrawing?.();
        },
        exitDrawMode() {
            this.activeTool = "";
            this.drawStatus = "Pick a tool and click on chart to draw";
            this.$refs.engine?.stopDrawing?.();
        },
        clearDrawings() {
            this.$refs.engine?.clearDrawings?.();
            this.drawStatus = "Cleared drawings";
            this.activeTool = "";
        },
        undoDrawing() {
            this.$refs.engine?.undoDrawing?.();
        },
        onTableColumns(cols) {
            this.$emit("table-columns", cols);
        },
        resize() {
            this.$nextTick(() => {
                requestAnimationFrame(() => {
                    try { this.chart?.applyNewSize?.(); } catch (e) { }
                    try { this.chart?.resize?.(); } catch (e) { }
                    try { this.$refs.engine?.chart?.resize?.(); } catch (e) { }
                    try { this.$refs.engine?.fitContent?.(); } catch (e) { }
                });
            });
        },
        onStudiesChanged(studies) {
            const list = Array.isArray(studies) ? studies : [];
            const cols = list.map(s => ({ key: s.key, label: s.label }));
            this.$emit("table-columns", cols);
            this.$refs.engine?.setActiveStudies?.(list);
            this.$emit("studies-change", list);
        },
        emitVisibleTableNow() {
            const cols = this.$refs.engine?.activeColumns?.map(({ key, label }) => ({ key, label })) || [];
            this.$emit("table-columns", cols);
            this.$refs.engine?.emitVisibleTableData?.();
        },
        onTableData(rows) {
            this.$emit("candles", rows);
        },
        setSymbol(ticker) {
            const sym = String(ticker || "").trim();
            if (!sym) return;
            this.selectedSymbol = sym;
            this.$refs.engine?.setSymbol?.(sym);
            this.emitToolbarState();
        },
        clamp(n, min, max) {
            return Math.max(min, Math.min(max, n));
        },
        getInfoCardSize() {
            const ref = this.$refs.infoCard;
            if (!ref) return { w: 0, h: 0 };
            const el = ref.$el || ref;
            const r = el?.getBoundingClientRect?.();
            if (!r) return { w: 0, h: 0 };
            return { w: r.width || 0, h: r.height || 0 };
        },
        getViewportMargin() {
            return this.clamp(Math.round(window.innerWidth * 0.01), 6, 16);
        },
        onEngineReady(ctx) {
            this.engineCtx = ctx;
            this.chart = ctx.chart;
            this.mainPaneId = ctx.getMainPaneId?.() || this.mainPaneId;
            this.$refs.engine?.setSymbol?.(this.selectedSymbol);
            this.emitToolbarState();
            this.$nextTick(() => {
                this.$refs.studies?.emitStudiesChanged?.();
                requestAnimationFrame(() => {
                    try { this.chart?.applyNewSize?.(); } catch (e) { }
                    try { this.chart?.resize?.(); } catch (e) { }
                });
            });
        },
        onCrosshairChange(payload) {
            if (!this.infoOn) {
                this.infoBox.visible = false;
                return;
            }
            if (!this.chart) return;
            const { x, y, paneId } = payload || {};
            if (x == null || y == null) {
                this.infoBox.visible = false;
                return;
            }
            const v = this.chart.convertFromPixel?.(
                { x, y },
                { paneId: paneId || this.mainPaneId }
            );
            const dataIndex = v?.dataIndex;
            if (dataIndex == null || dataIndex < 0) {
                this.infoBox.visible = false;
                return;
            }
            const list = this.chart.getDataList?.() || [];
            const k = list[dataIndex];
            if (!k) {
                this.infoBox.visible = false;
                return;
            }
            const engineEl = this.$refs.engine?.getRootEl?.();
            const rect = engineEl?.getBoundingClientRect?.();
            if (!rect) return;
            let left = rect.left + x + 12;
            let top = rect.top + y + 12;
            const margin = this.getViewportMargin();
            this.infoBox = {
                ...this.infoBox,
                visible: true,
                left,
                top,
                timeText: new Date(k.timestamp).toLocaleString(),
                open: k.open,
                high: k.high,
                low: k.low,
                close: k.close,
                volume: k.volume ?? 0,
            };
            this.$nextTick(() => {
                const { w: boxW, h: boxH } = this.getInfoCardSize();
                const maxLeft = window.innerWidth - (boxW || 0) - margin;
                const maxTop = window.innerHeight - (boxH || 0) - margin;
                const clampedLeft = this.clamp(left, margin, Math.max(margin, maxLeft));
                const clampedTop = this.clamp(top, margin, Math.max(margin, maxTop));
                if (!this.infoOn) return;
                this.infoBox = {
                    ...this.infoBox,
                    left: clampedLeft,
                    top: clampedTop,
                };
            });
        },
        applyChartType() {
            this.$refs.engine?.applyChartType?.();
        },
        emitToolbarState() {
            this.$emit("toolbar-state", {
                crosshairOn: this.crosshairBtnOn,
                infoOn: this.infoOn,
                tfLabel: this.tfLabel,
                chartTypeLabel: this.chartTypeLabel,
                symbolLabel: this.selectedSymbol,
            });
        },
        toggleCrosshair() {
            this.crosshairBtnOn = !this.crosshairBtnOn;
            this.emitToolbarState();
        },
        toggleInfo() {
            this.infoOn = !this.infoOn;
            if (!this.infoOn) this.infoBox.visible = false;
            this.emitToolbarState();
        },
        openMenu(kind, anchorEl) {
            this.$refs.studies?.openMenu?.(kind, anchorEl);
        },
        closeMenu() {
            this.$refs.studies?.closeMenu?.();
        },
        setTimeframe(t) {
            this.tf = t;
            this.emitToolbarState();
            this.$nextTick(() =>
                requestAnimationFrame(() => {
                    try { this.chart?.applyNewSize?.(); } catch (e) { }
                    try { this.chart?.resize?.(); } catch (e) { }
                })
            );
        },
        setChartType(t) {
            this.chartType = t;
            this.emitToolbarState();
        },
        onChartContextMenu(payload) {
            this.$refs.studies?.openContextMenu?.(payload);
        },
    },
};
</script>